import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useParticipant } from "@/context/ParticipantContext";
import { NAV } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_60dfbe01-e8f1-47bb-9c72-2a0d72443632/artifacts/waarlbny_DENSO-main-logo-web.jpg";

const TABS = [
  { to: "/home", label: "Home", testId: NAV.homeTab },
  { to: "/training-manual", label: "Training Manual", testId: NAV.trainingManualTab },
  { to: "/denso-catalogue", label: "Denso Catalogue", testId: NAV.productGuideTab },
];

export default function MainLayout({ children }) {
  const { participant, resetSession } = useParticipant();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleRestart = () => {
    resetSession();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#E4E4E7" }}>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="DENSO"
              data-testid={NAV.logo}
              className="h-9 w-auto rounded-sm"
            />
            <span className="hidden sm:block font-heading font-bold text-slate-800 text-sm tracking-wide">
              PRODUCT TRAINING
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                data-testid={tab.testId}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
                    isActive
                      ? "border-denso-red text-denso-red"
                      : "border-transparent text-slate-600 hover:text-denso-red"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {participant && (
              <div
                data-testid={NAV.participantBadge}
                className="text-right text-xs leading-tight"
              >
                <p className="font-semibold text-slate-800">
                  {participant.name} {participant.surname}
                </p>
              </div>
            )}
            <button
              onClick={handleRestart}
              data-testid={NAV.restartButton}
              title="Exit / switch participant"
              className="h-9 w-9 flex items-center justify-center rounded-md text-slate-500 hover:text-denso-red hover:bg-red-50 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>

          <button
            className="md:hidden h-9 w-9 flex items-center justify-center text-slate-700"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid={NAV.mobileMenuButton}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                data-testid={`mobile-${tab.testId}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-sm font-medium ${
                    isActive ? "bg-red-50 text-denso-red" : "text-slate-600"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
            {participant && (
              <div className="pt-2 border-t border-slate-100 mt-2 flex items-center justify-between">
                <div className="text-xs">
                  <p className="font-semibold text-slate-800">
                    {participant.name} {participant.surname}
                  </p>
                </div>
                <button
                  onClick={handleRestart}
                  className="text-xs text-denso-red font-medium flex items-center gap-1"
                >
                  <LogOut className="h-3.5 w-3.5" /> Exit
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-white border-t border-slate-200 py-4 text-center text-xs text-slate-400">
        DENSO Product Training Platform © {new Date().getFullYear()} — For Distributor & Staff Use Only
      </footer>
    </div>
  );
}
